/**
 * Records our OWN microphone during a voice call and posts it in short chunks
 * for transcription.
 *
 * Why the local mic and not the room's mixed audio: each participant
 * transcribing only themselves gives every line a speaker for free — the
 * uploader is the speaker — and needs no server-side media pipeline. LiveKit
 * keeps its direct browser-to-SFU path; this is a second, cheap consumer of
 * the same MediaStreamTrack.
 *
 * The recorder is restarted for every chunk rather than sliced with
 * `timeslice`: WebM/MP4 fragments after the first carry no container header
 * and cannot be decoded on their own. Stop/start yields self-contained files
 * at the cost of a few milliseconds of gap between chunks, which is
 * inaudible in a transcript.
 *
 * Chunks with no voice energy are dropped before upload. Whisper, handed
 * silence, invents subtitles-credits phrases rather than answering with
 * nothing, and every chunk is billed per minute either way — so a cheap
 * RMS check here is both the quality gate and the cost gate.
 */
import { Track, type Room } from 'livekit-client';
import { ApiError } from './api';
import { uploadVoiceChunk, type VoiceTranscriptSegment } from './voiceCallsApi';

/**
 * Longest a chunk may run. Chunks normally end earlier, at the first pause
 * after speech (see SILENCE_CUT_MS), so one line of transcript is one phrase;
 * this is the ceiling for someone who does not pause.
 */
const CHUNK_MS = 20_000;
/**
 * A pause this long after speech ends the chunk: the phrase is over. Long
 * enough that a breath mid-sentence does not split it — the model transcribes
 * a whole sentence far better than two halves that each lack the other.
 */
const SILENCE_CUT_MS = 1_500;
/** Never cut a chunk shorter than this — a "da" alone is not worth a request. */
const MIN_CHUNK_MS = 2_500;
/** How often the analyser is sampled while a chunk records. */
const VAD_SAMPLE_MS = 200;
/**
 * RMS (float samples, -1..1) a sample must reach to count as voice. Room tone
 * and keyboard noise sit around 0.002–0.008 on typical laptop mics; quiet
 * speech from arm's length is 0.02 and up.
 */
const VAD_THRESHOLD = 0.015;
/**
 * Voice must be present for at least this long, summed over the chunk, before
 * the chunk is uploaded. One sample over the threshold is a click, a cough or
 * a cup put down; handed that, the model does not answer with nothing, it
 * answers with a YouTube outro. Three samples is a spoken word.
 */
const MIN_VOICED_MS = 600;
/** Wait between attempts while there is no microphone track to record yet. */
const RETRY_MS = 1_000;

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

export type TranscriberStopReason =
  | 'stopped'
  | 'unsupported'
  | 'not-configured'
  | 'budget'
  | 'call-ended'
  /** The transcription provider itself refused (no credit, bad key); admins were alerted. */
  | 'upstream'
  | 'error';

export interface MicTranscriberHandle {
  /** Flush the chunk in progress, then stop. Resolves once the last upload has been sent. */
  stop: () => Promise<void>;
}

/** One recorded chunk, ready to be sent wherever this transcriber reports to. */
export interface MicChunk {
  audio: Blob;
  startedAt: Date;
  durationMs: number;
}

export interface MicTranscriberOptions {
  room: Room;
  /** The voice call to file chunks under. Ignored when `upload` is given. */
  callId?: string;
  /**
   * Where chunks go. Defaults to the voice-call endpoint; Firmio Meet passes
   * its own uploader, bound to the participant token, so the recorder itself
   * knows nothing about which feature it is transcribing for.
   */
  upload?: (chunk: MicChunk) => Promise<VoiceTranscriptSegment | null>;
  onSegment?: (segment: VoiceTranscriptSegment) => void;
  /** Fired once, when recording ends for any reason other than `stop()`. */
  onStopped?: (reason: TranscriberStopReason) => void;
}

/** True where the browser can record audio at all (not in every WebView). */
export function canTranscribeLocally(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof AudioContext !== 'undefined' &&
    MIME_CANDIDATES.some((m) => MediaRecorder.isTypeSupported(m))
  );
}

function pickMimeType(): string | null {
  return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) ?? null;
}

function micTrack(room: Room): MediaStreamTrack | null {
  const track = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track;
  const media = track?.mediaStreamTrack ?? null;
  return media && media.readyState === 'live' ? media : null;
}

/** Map an upload failure to whether we should keep going. */
function stopReasonFor(err: unknown): TranscriberStopReason | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code === 'AI_BUDGET_EXCEEDED') return 'budget';
  // A demo call once ran 46 minutes with every chunk refused by OpenAI and
  // nobody in the room told: the server now names that case, and we stop and
  // tell the speaker instead of retrying every 20 s into the same wall.
  if (err.code === 'TRANSCRIPTION_UNAVAILABLE') return 'upstream';
  if (err.status === 503) return 'not-configured';
  if (err.status === 409 || err.status === 404) return 'call-ended';
  if (err.status === 403 || err.status === 401) return 'error';
  // A 5xx or a network blip on one chunk is not a reason to give up the call.
  return null;
}

export function startMicTranscriber(options: MicTranscriberOptions): MicTranscriberHandle {
  const { room } = options;
  const send =
    options.upload ??
    ((chunk: MicChunk) => uploadVoiceChunk({ callId: options.callId ?? '', ...chunk }));
  const mimeType = pickMimeType();

  let stopped = false;
  let recorder: MediaRecorder | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let chunkTimer: ReturnType<typeof setTimeout> | null = null;
  let ctx: AudioContext | null = null;
  const pendingUploads = new Set<Promise<void>>();

  const finish = (reason: TranscriberStopReason) => {
    if (stopped) return;
    stopped = true;
    if (retryTimer) clearTimeout(retryTimer);
    if (chunkTimer) clearTimeout(chunkTimer);
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        // Already stopping; the onstop handler still flushes.
      }
    }
    if (reason !== 'stopped') options.onStopped?.(reason);
  };

  // Unclassified failures (a 500, a network blip) are tolerated one at a time,
  // but a run of them means the pipeline is down — stop rather than fail silently.
  let consecutiveFailures = 0;
  const MAX_CONSECUTIVE_FAILURES = 4;

  const upload = (audio: Blob, startedAt: Date, durationMs: number) => {
    const job = send({ audio, startedAt, durationMs })
      .then((segment) => {
        consecutiveFailures = 0;
        if (segment) options.onSegment?.(segment);
      })
      .catch((err: unknown) => {
        const reason = stopReasonFor(err);
        if (reason) finish(reason);
        else if (++consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) finish('error');
      })
      .finally(() => pendingUploads.delete(job));
    pendingUploads.add(job);
  };

  const cycle = () => {
    if (stopped) return;
    const track = micTrack(room);
    if (!track || !mimeType) {
      // No mic yet (permission still pending, or denied and we're listening
      // only). Keep looking: the user may allow it later in the call.
      retryTimer = setTimeout(cycle, RETRY_MS);
      return;
    }

    const stream = new MediaStream([track]);
    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(stream, { mimeType });
    } catch {
      finish('unsupported');
      return;
    }
    recorder = rec;

    // Voice-energy gate for this chunk. `micWasOn` is sampled alongside: a
    // mute in the last second must not throw away nineteen seconds of speech.
    // `lastVoiceAt` is what ends a chunk at a pause, so lines follow phrases.
    let voicedMs = 0;
    let micWasOn = false;
    let lastVoiceAt = 0;
    const chunkStartedMs = Date.now();
    let vadTimer: ReturnType<typeof setInterval> | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    try {
      ctx ??= new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      vadTimer = setInterval(() => {
        if (room.localParticipant.isMicrophoneEnabled) micWasOn = true;
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) sum += buf[i]! * buf[i]!;
        const rms = Math.sqrt(sum / buf.length);
        const now = Date.now();
        if (rms >= VAD_THRESHOLD) {
          lastVoiceAt = now;
          voicedMs += VAD_SAMPLE_MS;
        } else if (
          lastVoiceAt > 0 &&
          now - lastVoiceAt >= SILENCE_CUT_MS &&
          now - chunkStartedMs >= MIN_CHUNK_MS &&
          rec.state === 'recording'
        ) {
          // Speech, then a pause: close the phrase and start listening for
          // the next one. onstop uploads this one and begins the next cycle.
          rec.stop();
        }
      }, VAD_SAMPLE_MS);
    } catch {
      // No analyser (very old WebKit): upload every chunk and let the server's
      // artefact filter do what it can.
      voicedMs = Number.POSITIVE_INFINITY;
      micWasOn = true;
    }

    const startedAt = new Date();
    const parts: Blob[] = [];
    rec.ondataavailable = (e: BlobEvent) => {
      if (e.data.size > 0) parts.push(e.data);
    };
    rec.onerror = () => {
      // The track ended under us (device unplugged, LiveKit swapped it on
      // unmute). Drop this chunk and start over on the current track.
      if (chunkTimer) clearTimeout(chunkTimer);
    };
    rec.onstop = () => {
      if (vadTimer) clearInterval(vadTimer);
      if (chunkTimer) clearTimeout(chunkTimer);
      source?.disconnect();
      if (recorder === rec) recorder = null;

      const durationMs = Date.now() - startedAt.getTime();
      const spoke = voicedMs >= MIN_VOICED_MS;
      // A muted mic yields silence anyway; the flag guards against the
      // analyser being fooled by a bump on the desk while muted.
      if (parts.length > 0 && spoke && micWasOn && durationMs >= 500) {
        upload(new Blob(parts, { type: mimeType }), startedAt, durationMs);
      }
      if (!stopped) cycle();
    };

    try {
      rec.start();
    } catch {
      if (vadTimer) clearInterval(vadTimer);
      source?.disconnect();
      retryTimer = setTimeout(cycle, RETRY_MS);
      return;
    }
    chunkTimer = setTimeout(() => {
      if (rec.state === 'recording') rec.stop();
    }, CHUNK_MS);
  };

  if (!mimeType) {
    // Report on the next tick so the caller has its handle first.
    setTimeout(() => finish('unsupported'), 0);
  } else {
    cycle();
  }

  return {
    stop: async () => {
      finish('stopped');
      // Give the final onstop a moment to fire and enqueue its upload…
      await new Promise((resolve) => setTimeout(resolve, 50));
      // …then let every upload in flight finish before the caller moves on.
      await Promise.allSettled([...pendingUploads]);
      if (ctx) {
        await ctx.close().catch(() => undefined);
        ctx = null;
      }
    },
  };
}
